import Vue from "vue";
import Vuex from "vuex";
import { flatten } from "lodash";
import * as types from "./mutation-types";
import VuexPersistence from "vuex-persist";
import AssignAlphaID from "@Shared/utils/AlphaID";
import FeatureFlags from "@Shared/utils/FeatureFlags";
import { analyticsMiddleware, event } from "@cb/vue-google-analytics";
import router from "../router";
import {
  KEYS,
  getCustomBrandingByKey,
  getAllPopoverText,
} from "@Shared/utils/CustomBranding";
import API from "../constants.js";
import * as Cookies from "js-cookie";

Vue.use(Vuex);

const BASE_API_URL = API.patientEnrollment;

const BASE_API_REST_URL = process.env.VUE_APP_API_REST_SERVER;
const SESSION_LIFE_MS = process.env.VUE_APP_SESSION_LIFE_MS;
const SESSION_WARNING_MS = process.env.VUE_APP_SESSION_WARNING_MS;
const SESSION_BUFFER_MS = process.env.VUE_APP_SESSION_BUFFER_MS;

const vuexSession = new VuexPersistence({
  key: "vuex-hsf",
  storage: window.sessionStorage,
  reducer: (state) => ({
    featureFlags: state.featureFlags,
    showFeedbackModal: state.showFeedbackModal,
    feedbackDetails: state.feedbackDetails,
  }),
});

export default new Vuex.Store({
  state: {
    enrollmentCurrentSearchStepIndex: 0,
    enrollmentCurrentStepIndex: 0,
    editFlag: false,
    basicEditFlag: false, // Sometimes we need to know if the user went to edit basic info
    guid: null,
    guidError: false,
    requiresUserAuth: false,
    searchedProviders: [],
    activeForms: [],
    errors: {},
    selectedProvider: {},
    requestStatus: {
      searchedProviders: { active: false, error: false },
      selectedProvider: { active: false, error: false },
      formNextLoading: { active: false, error: false },
      submission: { active: false, error: false },
    },
    formValues: {},
    attempts: 0,
    uneditedFormValues: {}, //used for editing functionality
    pageNumber: 0,
    pageSize: 4,
    acceptedTermsAndConditions: false,
    consentToContact: false,
    termsAndConditionsError: false,
    featureFlags: null,
    fileMethodError: false,
    fileMethodUserID: undefined,
    frpAdditionalInfo: {
      physicalAddress: false,
      previousAddress: false,
    },
    uneditedFRPAdditionalInfo: {
      physicalAddress: false,
      previousAddress: false,
    },
    showFRPAdditionalInformation: false,
    recommendedAddresses: {},
    accountNumRegex: true,
    submissionComplete: false,
    showSessionWarningModal: false,
    timeoutElement: 0,
    searchProviderView: true,
    timeoutFlag: false,
    existingHSFaccount: "",
    minCreditLimit: 0.0,
    uneditedexistingHSFAccount: "",
    emailVerficationStatus: 'NoData',
    selfEnrollmentID: 0,
    showFeedbackModal: true,
    feedbackDetails: {},
    enrollmentSource: "",
    billingStatementImage: "",
  },
  getters: {
    enrollmentCurrentSearchStepIndex: (state) =>
      state.enrollmentCurrentSearchStepIndex,
    enrollmentCurrentStepIndex: (state) => state.enrollmentCurrentStepIndex,
    editFlag: (state) => state.editFlag,
    basicEditFlag: (state) => state.basicEditFlag,
    guid: (state) => state.guid,
    guidError: (state) => state.guidError,
    requiresUserAuth: (state) => state.requiresUserAuth,
    searchedProviders: (state) => state.searchedProviders,
    showSessionWarningModal: (state) => state.showSessionWarningModal,
    searchProviderView: (state) => state.searchProviderView,
    activeErrors: (state) =>
      flatten(state.activeForms.map((x) => state.errors[x] || [])),
    selectedProvider: (state) => state.selectedProvider,
    formValues: (state) => state.formValues,
    uneditedFormValues: (state) => state.uneditedFormValues,
    requestStatus: (state) => state.requestStatus,
    pageNumber: (state) => state.pageNumber,
    pageSize: (state) => state.pageSize,
    paginatedSearchedProviders: (state) => {
      const start = state.pageNumber * state.pageSize;
      const end = start + state.pageSize;
      return state.searchedProviders.slice(start, end);
    },
    acceptedTermsAndConditions: (state) => state.acceptedTermsAndConditions,
    consentToContact: (state) => state.consentToContact,
    termsAndConditionsError: (state) => state.termsAndConditionsError,
    amountRequested: (state) => {
      if (state.formValues.account) {
        return state.formValues.account.amtRequested;
      }
      return "";
    },
    featureFlags: (state) => state.featureFlags,
    fileMethodError: (state) => state.fileMethodError,
    attempts: (state) => state.attempts,
    fileMethodUserID: (state) => state.fileMethodUserID,
    physicalMailingAddressCheckbox: (state) =>
      state.frpAdditionalInfo.physicalAddress,
    previousAddressCheckbox: (state) => state.frpAdditionalInfo.previousAddress,
    uneditedPhysicalMailingAddressCheckbox: (state) =>
      state.uneditedFRPAdditionalInfo.physicalAddress,
    uneditedPreviousAddressCheckbox: (state) =>
      state.uneditedFRPAdditionalInfo.previousAddress,
    showFRPAdditionalInformation: (state) => state.showFRPAdditionalInformation,
    recAddresses: (state) => (addrType) =>
      state.recommendedAddresses[addrType] || [],
    address: (state) => (addrType) => state.formValues[addrType] || {},
    accountNumRegex: (state) => state.accountNumRegex,
    submissionComplete: (state) => state.submissionComplete,
    timeoutFlag: (state) => state.timeoutFlag,
    existingHSFAccount: (state) => state.existingHSFaccount,
    uneditedexistingHSFAccount: (state) => state.uneditedexistingHSFAccount,
    emailVerficationStatus: (state) => state.emailVerficationStatus,
    selfEnrollmentID: (state) => state.selfEnrollmentID,
    showFeedbackModal: state => state.showFeedbackModal,
    getFeedbackDetails: state => state.feedbackDetails,
    getEnrollmentSource: state => state.enrollmentSource,
    getBillingStatementImage: state => state.billingStatementImage,
  },
  mutations: {
    [types.INCREMENT_ENROLLMENT_CURRENT_SEARCH_STEP_INDEX](state) {
      state.enrollmentCurrentSearchStepIndex++;
    },
    [types.DECREMENT_ENROLLMENT_CURRENT_SEARCH_STEP_INDEX](state) {
      state.enrollmentCurrentSearchStepIndex--;
    },
    [types.INCREMENT_ENROLLMENT_CURRENT_STEP_INDEX](state) {
      state.enrollmentCurrentStepIndex++;
    },
    [types.DECREMENT_ENROLLMENT_CURRENT_STEP_INDEX](state) {
      state.enrollmentCurrentStepIndex--;
    },
    [types.SET_EDIT_FLAG](state, value) {
      state.editFlag = value;
    },
    [types.SET_BASIC_EDIT_FLAG](state, value) {
      state.basicEditFlag = value;
    },
    [types.SET_GUID](state, { guid }) {
      state.guid = guid;
    },
    [types.SET_GUID_ERROR](state, { isError }) {
      state.guidError = isError;
    },
    [types.SET_REQUIRES_USER_AUTH](state, requiresUserAuth) {
      state.requiresUserAuth = requiresUserAuth;
    },
    [types.SET_SEARCHED_PROVIDERS](state, searchedProviders) {
      state.searchedProviders = searchedProviders;
    },
    [types.SET_FORM_ERRORS](state, { formName, errors }) {
      state.errors = {
        ...state.errors,
        [formName]: errors,
      };
    },

    [types.SET_SELECTED_PROVIDER](state, { provider }) {
      Vue.set(state, "selectedProvider", provider);

      if (state.existingHSFaccount) {
        state.minCreditLimit = 0.0;
      } else state.minCreditLimit = state.selectedProvider.minimumCreditLimit;
    },

    [types.SET_ACTIVE_FORMS](state, forms) {
      state.activeForms = [...forms];
    },
    [types.SET_FORM_VALUES](state, value) {
      state.formValues = { ...value };
    },
    [types.SET_UNEDITED_FORM_VALUES](state, value) {
      state.uneditedFormValues = { ...value };
    },
    [types.SET_SESSION_MODAL](state, value) {
      state.showSessionWarningModal = value;
    },
    [types.SET_SEARCH_VIEW](state, value) {
      state.searchProviderView = value;
    },
    [types.SET_REQUEST_STATUS](state, { propertyName, status }) {
      state.requestStatus[propertyName] = {
        ...state.requestStatus[propertyName],
        ...status,
      };
    },

    [types.SET_EXISTING_HSF_ACCOUNT](state, value) {
      state.existingHSFaccount = value.value;
      if (state.existingHSFaccount === "true") {
        state.minCreditLimit = 0.0;
      } else state.minCreditLimit = state.selectedProvider.minimumCreditLimit;
    },

    [types.SAVE_EXISTING_HSF_ACCOUNT](state, value) {
      state.existingHSFaccount = value.value;
      state.uneditedexistingHSFAccount = value.value;
      state.formValues.account.existingHSFAccount =
        state.existingHSFaccount === "true" ? "Yes" : "No";
      if (state.existingHSFaccount) {
        state.minCreditLimit = 0.0;
      } else state.minCreditLimit = state.selectedProvider.minimumCreditLimit;
    },

    [types.RESET_FORMS](state) {
      if (state.formValues != null && typeof state.formValues !== "undefined") {
        if (
          state.formValues.authentication !== null &&
          typeof state.formValues.authentication !== "undefined"
        ) {
          state.formValues.authentication.dob = "";
          state.formValues.authentication.ssn = "";
          state.formValues.authentication.accNum = "";
        }

        if (
          state.formValues.basic !== null &&
          typeof state.formValues.basic !== "undefined"
        ) {
          state.formValues.basic.dateOfBirth = "";
          state.formValues.basic.emailAddress = "";
          state.formValues.basic.firstName = "";
          state.formValues.basic.lastName = "";
          state.formValues.basic.middleInitial = "";
          state.formValues.basic.mobileNumber = "";
          state.formValues.basic.phoneNumber = "";
          state.formValues.basic.workNumber = "";
          state.formValues.basic.ssn = "";
        }

        if (
          state.formValues.physical !== null &&
          typeof state.formValues.physical !== "undefined"
        ) {
          state.formValues.physical.address1 = "";
          state.formValues.physical.address2 = "";
          state.formValues.physical.addressType = "";
          state.formValues.physical.city = "";
          state.formValues.physical.state = "";
          state.formValues.physical.zipCode = "";
        }

        if (state.formValues.mailing != undefined) {
          state.formValues.mailing = undefined;
        }
        if (state.formValues.previous != undefined) {
          state.formValues.previous = undefined;
        }

        if (
          state.formValues.account !== null &&
          typeof state.formValues.account !== "undefined"
        ) {
          state.formValues.account.amtRequested = "";
          state.formValues.account.patientName = "";
          state.formValues.account.actNumber = "";
          state.formValues.account.existingHSFAccount = "";
        }
      }

      state.frpAdditionalInfo.physicalAddress = false;
      state.frpAdditionalInfo.previousAddress = false;
      state.uneditedFRPAdditionalInfo.physicalAddress = false;
      state.uneditedFRPAdditionalInfo.previousAddress = false;
      state.showFRPAdditionalInformation = false;
      state.enrollmentCurrentStepIndex = 0;
      state.existingHSFaccount = "";
    },
    [types.RESET_ERRORS](state) {
      state.errors = {};
    },
    [types.SET_PAGE_NUMBER](state, number) {
      state.pageNumber = number;
    },
    [types.SET_PAGE_SIZE](state, size) {
      state.pageSize = size;
    },
    [types.SET_TERMS_AND_CONDITIONS_STATUS](state, status) {
      state.acceptedTermsAndConditions = status;
    },
    [types.SET_CONSENT_TO_CONTACT](state, value) {
      state.consentToContact = value;
    },
    [types.SET_TERMS_AND_CONDITIONS_ERROR](state, { hasError }) {
      state.termsAndConditionsError = hasError;
    },
    [types.SET_FEATURE_FLAGS](state, { featureFlags }) {
      state.featureFlags = featureFlags;
    },
    [types.SET_FILE_METHOD_ERROR](state, value) {
      state.fileMethodError = value;
    },
    [types.SET_FILE_METHOD_USER_ID](state, value) {
      state.fileMethodUserID = value;
    },
    [types.SET_PHYSICAL_MAILING_ADDRESS_CHECKBOX](state, { value }) {
      Vue.set(state.frpAdditionalInfo, "physicalAddress", value);
    },
    [types.SET_PREVIOUS_ADDRESS_CHECKBOX](state, { value }) {
      Vue.set(state.frpAdditionalInfo, "previousAddress", value);
    },
    [types.SET_UNEDITED_PHYSICAL_MAILING_ADDRESS_CHECKBOX](state, value) {
      state.uneditedFRPAdditionalInfo.physicalAddress = value;
    },
    [types.SET_UNEDITED_PREVIOUS_ADDRESS_CHECKBOX](state, value) {
      state.uneditedFRPAdditionalInfo.previousAddress = value;
    },
    [types.SET_SHOW_FRP_ADDITIONAL_INFORMATION](state, value) {
      state.showFRPAdditionalInformation = value;
    },
    [types.ADD_RECOMENDED_ADDR](state, { type, addr }) {
      state.recommendedAddresses = {
        ...state.recommendedAddresses,
        [type]: addr,
      };
    },
    [types.SET_ACCOUNT_NUMBER_REGEX](state, regex) {
      state.accountNumRegex = regex;
    },
    [types.SET_STATEMENT_IMG](state, img) {
      state.statementImg = img;
    },
    [types.SET_SUBMISSION_COMPLETE](state, { isComplete }) {
      state.submissionComplete = isComplete;
    },
    [types.RESET_CURRENT_SEARCH_STEP_INDEX](state) {
      state.enrollmentCurrentSearchStepIndex = 0;
    },
    [types.RESET_CURRENT_STEP_INDEX](state) {
      state.enrollmentCurrentStepIndex = 0;
    },
    [types.SET_TIMEOUT_FLAG](state, value) {
      state.timeoutFlag = value;
    },
    [types.SET_EMAIL_VERIFICATION_STATUS](state, value) {
      state.emailVerficationStatus = value;
    },
    [types.SET_SELF_ENROLLMENT_APP_ID](state, value) {
      state.selfEnrollmentID = value;
    },
    [types.SET_SHOW_FEEDBACK_MODAL](state, value) {
      state.showFeedbackModal = value;
    },
    [types.SET_FEEDBACK_DETAILS](state, value) {
      state.feedbackDetails = value;
    },
    [types.SET_ENROLLMENT_SOURCE](state, value) {
      state.enrollmentSource = value;
    },
    [types.SET_BILLING_STATEMENT_IMAGE]: (state, value) => {
      state.billingStatementImage = value;
    },
  },
  actions: {
    incrementenrollmentCurrentSearchStepIndex({ commit }) {
      commit(types.INCREMENT_ENROLLMENT_CURRENT_SEARCH_STEP_INDEX, {
        meta: {
          analytics: [
            [
              "event",
              {
                eventCategory: "State Change (API)",
                eventAction: "Current Step",
                eventLabel: "Increment",
              },
            ],
          ],
        },
      });
    },

    decrementenrollmentCurrentSearchStepIndex({ commit }) {
      commit(types.DECREMENT_ENROLLMENT_CURRENT_SEARCH_STEP_INDEX, {
        meta: {
          analytics: [
            [
              "event",
              {
                eventCategory: "State Change (API)",
                eventAction: "Current Step",
                eventLabel: "Decrement",
              },
            ],
          ],
        },
      });
    },

    incrementEnrollmentCurrentStepIndex({ commit }) {
      commit(types.INCREMENT_ENROLLMENT_CURRENT_STEP_INDEX, {
        meta: {
          analytics: [
            [
              "event",
              {
                eventCategory: "State Change (API)",
                eventAction: "Current Step",
                eventLabel: "Increment",
              },
            ],
          ],
        },
      });
    },

    decrementEnrollmentCurrentStepIndex({ commit }) {
      commit(types.DECREMENT_ENROLLMENT_CURRENT_STEP_INDEX, {
        meta: {
          analytics: [
            [
              "event",
              {
                eventCategory: "State Change (API)",
                eventAction: "Current Step",
                eventLabel: "Decrement",
              },
            ],
          ],
        },
      });
    },

    setGuid({ commit }, guid) {
      commit(types.SET_GUID, {
        guid: guid,
        meta: {
          analytics: [
            [
              "event",
              {
                eventCategory: "State Change (API)",
                eventAction: "Guid",
                eventLabel: guid,
              },
            ],
          ],
        },
      });
    },

    createTimer({ commit, state }, milliseconds) {
      commit(types.SET_TIMEOUT_FLAG, false);
      milliseconds =
        typeof milliseconds === "undefined"
          ? SESSION_LIFE_MS - SESSION_WARNING_MS - SESSION_BUFFER_MS
          : milliseconds;
      if (!state.submissionComplete) {
        if (state.timeoutElement !== 0) {
          clearTimeout(state.timeoutElement);
        }

        state.timeoutElement = setTimeout(() => {
          commit(types.SET_SESSION_MODAL, true);
        }, milliseconds);
      }
    },
    renewTimer({ commit, dispatch }) {
      commit(types.SET_SESSION_MODAL, false);
      dispatch("createTimer");
    },
    logout({ commit, dispatch, state }) {
      //logging user out of their current session
      commit(types.SET_SESSION_MODAL, false);
      commit(types.SET_SEARCH_VIEW, false);
      commit(types.SET_FILE_METHOD_ERROR, false);
      if (state.timeoutElement !== 0) clearTimeout(state.timeoutElement);

      dispatch("resetCurrentSearchStepIndex");
      dispatch("resetCurrentStepIndex");
      dispatch("resetForms");
      commit(types.SET_TIMEOUT_FLAG, true);
      router.push({ name: "enroll", query: { guid: state.guid } });
    },
    setRequiresUserAuth({ commit }, requiresUserAuth) {
      commit(types.SET_REQUIRES_USER_AUTH, requiresUserAuth);
    },
    resetSearchedProviders({ commit }) {
      commit(types.SET_SEARCHED_PROVIDERS, []);
    },
    setSearchedProviders(
      { state, commit, dispatch },
      { searchZipCode, searchRadius }
    ) {
      commit(types.SET_REQUEST_STATUS, {
        propertyName: "searchedProviders",
        status: { active: true, error: false },
      });

      Vue.axios
        .get(`${BASE_API_URL}/InstitutionLocations/GetInstitutions`, {
          params: {
            zipCode: searchZipCode,
            radius: searchRadius,
          },
        })

        .then((response) => {
          commit(types.SET_SEARCHED_PROVIDERS, AssignAlphaID(response.data));
          dispatch("resetSelectedProvider");
          commit(types.SET_REQUEST_STATUS, {
            propertyName: "searchedProviders",
            status: { active: false },
          });

          if (state.enrollmentCurrentSearchStepIndex === 0) {
            commit(types.INCREMENT_ENROLLMENT_CURRENT_SEARCH_STEP_INDEX);
          }
        })
        .catch((err) => {
          console.error("Error searching for providers.", err);

          commit(types.SET_REQUEST_STATUS, {
            propertyName: "searchedProviders",
            status: { active: false, error: true },
          });
        });
    },
    setFormErrors({ commit }, { formName, errors }) {
      commit(types.SET_FORM_ERRORS, {
        formName,
        errors,
      });
    },
    setActiveForms({ commit }, forms) {
      commit(types.SET_ACTIVE_FORMS, forms);
    },
    setFormValues({ commit }, value) {
      commit(types.SET_FORM_VALUES, value);
    },
    resetForms({ commit }) {
      commit(types.RESET_FORMS);
    },
    resetErrors({ commit }) {
      commit(types.RESET_ERRORS);
    },
    clearSession({ commit, state }) {
      commit(types.RESET_FORMS);
      commit(types.SET_SESSION_MODAL, false);

      if (state.timeoutElement !== 0) clearTimeout(state.timeoutElement);
    },
    setSelectedProviderByGuid({ commit, state }, guid) {
      commit(types.SET_SEARCH_VIEW, false);
      commit(types.SET_REQUEST_STATUS, {
        propertyName: "selectedProvider",
        status: { active: true },
      });

      Vue.axios
        .get(`${BASE_API_URL}/Institution/GetInstitutionByGUID`, {
          headers: {
            session: Cookies.get("guid"),
            guid: guid,
            "Cache-Control": "no-cache",
            Pragma: "no-cache",
            Expires: "0",
          },
        })
        .then((response) => {
          let provider = response.data;
          if (provider.spid > 0) {
            Cookies.set("guid", provider.guid);
            commit(types.SET_SELECTED_PROVIDER, {
              provider: {
                ...provider,
                popovers: getAllPopoverText(provider.customBrandingList),
              },
              meta: {
                analytics: [
                  [
                    "event",
                    {
                      eventCategory: "State Change (API)",
                      eventAction: "Select Provider",
                      eventLabel: "GUID",
                    },
                  ],
                ],
              },
            });
            commit(
              types.SET_STATEMENT_IMG,
              getCustomBrandingByKey(
                KEYS.STATEMENT_IMG,
                provider.customBrandingList
              )
            );
            commit(
              types.SET_ACCOUNT_NUMBER_REGEX,
              getCustomBrandingByKey(
                KEYS.ACCOUNT_NUM_REGEX,
                provider.customBrandingList
              )
            );
            if (state.enrollmentCurrentSearchStepIndex === 1) {
              commit(types.INCREMENT_ENROLLMENT_CURRENT_SEARCH_STEP_INDEX);
            }
          } else {
            commit(types.SET_GUID, { guid: null });
            commit(types.SET_GUID_ERROR, { isError: true });
            commit(types.SET_SEARCH_VIEW, true);
            router.replace({ name: "search" });
          }
          commit(types.SET_REQUEST_STATUS, {
            propertyName: "selectedProvider",
            status: { active: false },
          });
        })
        .catch((err) => {
          console.error("Error getting provider by GUID");
          commit(types.SET_REQUEST_STATUS, {
            propertyName: "selectedProvider",
            status: { active: false },
          });
        });
    },
    setSelectedProviderBySpid({ commit, state }, setProvider) {
      commit(types.SET_REQUEST_STATUS, {
        propertyName: "selectedProvider",
        status: { active: true },
      });

      Vue.axios
        .get(`${BASE_API_URL}/Institution/GetInstitutionBySPID`, {
          headers: {
            session: Cookies.get("guid"),
            spid: setProvider.spid,
            locId: setProvider.location.locationID,
            "Cache-Control": "no-cache",
            Pragma: "no-cache",
            Expires: "0",
          },
        })
        .then((response) => {
          let provider = response.data;
          if (provider.spid > 0) {
            Cookies.set("guid", provider.guid);
            commit(types.SET_FILE_METHOD_ERROR, false);
            commit(types.SET_SELECTED_PROVIDER, {
              provider: {
                ...provider,
                popovers: getAllPopoverText(provider.customBrandingList),
                isHSF: setProvider.isHSF,
                isCommerceCare: setProvider.isCommerceCare
              },
              meta: {
                analytics: [
                  [
                    "event",
                    {
                      eventCategory: "State Change (API)",
                      eventAction: "Select Provider",
                      eventLabel: "SPID",
                    },
                  ],
                ],
              },
            });
            commit(
              types.SET_STATEMENT_IMG,
              getCustomBrandingByKey(
                KEYS.STATEMENT_IMG,
                provider.customBrandingLists
              )
            );
            commit(types.SET_GUID, { guid: provider.guid });
            commit(
              types.SET_ACCOUNT_NUMBER_REGEX,
              getCustomBrandingByKey(
                KEYS.ACCOUNT_NUM_REGEX,
                provider.customBrandingList
              )
            );
            if (state.enrollmentCurrentSearchStepIndex === 1) {
              commit(types.INCREMENT_ENROLLMENT_CURRENT_SEARCH_STEP_INDEX);
            }
          }
          commit(types.SET_REQUEST_STATUS, {
            propertyName: "selectedProvider",
            status: { active: false },
          });
        })
        .catch(() => {
          console.error("Error getting provider by SPID");
          //  console.error(err);
          commit(types.SET_REQUEST_STATUS, {
            propertyName: "selectedProvider",
            status: { active: false },
          });
        });
    },
    resetSelectedProvider({ commit, state }) {
      if (state.timeoutElement !== 0) clearTimeout(state.timeoutElement);

      commit(types.SET_SELECTED_PROVIDER, {
        provider: {},
        meta: {
          analytics: [
            [
              "event",
              {
                eventCategory: "State Change (API)",
                eventAction: "Clear Provider",
              },
            ],
          ],
        },
      });

      commit(types.SET_REQUEST_STATUS, {
        propertyName: "selectedProvider",
        status: { active: false },
      });

      if (state.enrollmentCurrentSearchStepIndex === 2) {
        commit(types.DECREMENT_ENROLLMENT_CURRENT_SEARCH_STEP_INDEX);
      }
    },
    fileMethodAuth({ commit, state }) {
      return new Promise((resolve, reject) => {
        commit(types.SET_REQUEST_STATUS, {
          propertyName: "formNextLoading",
          status: { active: true },
        });

        commit(types.SET_TIMEOUT_FLAG, false);

        let data = {
          DateOfBirth: state.formValues.authentication.dob,
          SSN: state.formValues.authentication.ssn,
          accountNum: state.formValues.authentication.accNum,
          providerId: state.selectedProvider.guid,
          spid: state.selectedProvider.spid,
        };

        Vue.axios
          .post(`${BASE_API_URL}/FileMethod/Authentication`, data, {
            headers: {
              session: Cookies.get("guid"),
            },
          })
          .then((response) => {
            commit(types.SET_REQUEST_STATUS, {
              propertyName: "formNextLoading",
              status: { active: false },
            });
            this.state.attempts++;
            if (response.data != null && response.data != "") {
              commit(types.SET_FILE_METHOD_ERROR, false);
              commit(types.SET_FILE_METHOD_USER_ID, response.data);
              Cookies.set("acctid", response.data);
              Cookies.set("providerId", data.providerId);
              resolve();
            } else {
              Cookies.set("acctid", "");
              Cookies.set("providerId", "");
              commit(types.SET_FILE_METHOD_ERROR, true);
              reject();
            }
          })
          .catch((err) => {
            commit(types.SET_FILE_METHOD_ERROR, true);
            commit(types.SET_REQUEST_STATUS, {
              propertyName: "formNextLoading",
              status: { active: false },
            });
            if (err.response.status === 406) {
              this.state.attempts = 0;
              router
                .push({
                  name: "enroll-forms",
                  params: { step: "frp-information" },
                })
                .catch(() => { });
            }
            reject(err);
          });
      });
    },
    setRequestStatus({ commit }, { propertyName, status }) {
      commit(types.SET_REQUEST_STATUS, {
        propertyName,
        status: { active: status },
      });
    },
    setPageNumber({ commit }, number) {
      commit(types.SET_PAGE_NUMBER, number);
    },
    setPageSize({ commit }, size) {
      commit(types.SET_PAGE_SIZE, size);
    },
    setTermsAndConditionsStatus({ commit }, status) {
      commit(types.SET_TERMS_AND_CONDITIONS_STATUS, status);
    },
    setConsentToContact({ commit }, status) {
      commit(types.SET_CONSENT_TO_CONTACT, status);
    },
    setTermsAndConditionsError({ commit }, hasError) {
      commit(types.SET_TERMS_AND_CONDITIONS_ERROR, {
        hasError,
        meta: {
          analytics: [
            [
              "event",
              {
                eventCategory: "State Change (API)",
                eventAction: "Terms and Conditions Error",
                eventLabel: hasError ? "Shown" : "Hidden",
              },
            ],
          ],
        },
      });
    },
    setFeatureFlags({ commit }) {
      return new Promise((resolve, reject) => {
        Vue.axios
          .get(`${BASE_API_URL}/FeatureSwitch/RetrieveAll`)
          .then((response) => {
            commit(types.SET_FEATURE_FLAGS, {
              featureFlags: FeatureFlags.filteredMapFeatureFlags(response.data),
              meta: {
                analytics: [
                  [
                    "event",
                    {
                      eventCategory: "State Change (API)",
                      eventAction: "Get Feature Flags",
                      eventLabel: "Global",
                    },
                  ],
                ],
              },
            });
            resolve();
          })
          .catch((err) => {
            console.error("Unable to get feature flags.");
            reject(err);
          });
      });
    },
    setPhysicalMailingAddressCheckbox({ commit }, value) {
      commit(types.SET_PHYSICAL_MAILING_ADDRESS_CHECKBOX, {
        value: value,
        meta: {
          analytics: [
            [
              "event",
              {
                eventCategory: "State Change",
                eventAction: "Physical Mailing Address Checkbox",
                eventLabel: value ? "Checked" : "Unchecked",
              },
            ],
          ],
        },
      });
    },
    setPreviousAddressCheckbox({ commit }, value) {
      commit(types.SET_PREVIOUS_ADDRESS_CHECKBOX, {
        value: value,
        meta: {
          analytics: [
            [
              "event",
              {
                eventCategory: "State Change",
                eventAction: "Previous Address Checkbox",
                eventLabel: value ? "Checked" : "Unchecked",
              },
            ],
          ],
        },
      });
    },

    setExistingHSFAccount({ commit }, value) {
      commit(types.SET_EXISTING_HSF_ACCOUNT, {
        value: value,
        meta: {
          analytics: [
            [
              "event",
              {
                eventCategory: "State Change",
                eventAction: "Previous Existing HSF",
                eventLabel: value ? "Checked" : "Unchecked",
              },
            ],
          ],
        },
      });
      //state.formValues.account.existingHSFAccount = (state.existingHSFaccount === "true") ? "Yes" : "No";
    },

    saveExistingHSFAccount({ commit }, value) {
      commit(types.SAVE_EXISTING_HSF_ACCOUNT, {
        value: value,
        meta: {
          analytics: [
            [
              "event",
              {
                eventCategory: "State Change",
                eventAction: "Previous Existing HSF",
                eventLabel: value ? "Checked" : "Unchecked",
              },
            ],
          ],
        },
      });
    },

    validateAddress({ commit, state }, addressType) {
      return new Promise((resolve, reject) => {
        Vue.axios
          .post(`${BASE_API_URL}/Address/CheckAddress`, {
            address1: state.formValues[addressType].address1,
            address2: state.formValues[addressType].address2,
            city: state.formValues[addressType].city,
            state: state.formValues[addressType].state,
            zipcode: state.formValues[addressType].zipCode.split("-")[0],
            plus4: state.formValues[addressType].zipCode.split("-")[1],
          })
          .then((response) => {
            commit(types.ADD_RECOMENDED_ADDR, {
              addr: response.data.addresses || [],
              type: addressType,
              meta: {
                analytics: [
                  [
                    "event",
                    {
                      eventCategory: "State Change (API)",
                      eventAction: "Address Validation",
                      eventLabel: "put valid flag here",
                    },
                  ],
                ],
              },
            });

            resolve(
              response.data.isValid && !response.data.recommendedAddr
                ? "valid"
                : addressType
            );
          })
          .catch((err) => {
            reject(err);
          });
      });
    },

    getTokenInfo({ commit }, token) {
      // call api
      return new Promise((resolve, reject) => {

        Vue.axios
          .get(`${BASE_API_URL}/Enrollment/getTokenInformation`, {
            params: {
              token: encodeURI(token)
            },
            headers: { 'content-type': 'application/x-www-form-urlencoded' }
          })
          .then(response => {
            commit(types.SET_EMAIL_VERIFICATION_STATUS, response.data);

            emailVerficationStatus = response.data;


          })
          .catch(err => { });
      })

    },



    submitEnrollment({ commit, state }) {
      //clear timer
      commit(types.SET_SESSION_MODAL, false);
      if (state.timeoutElement !== 0) {
        clearTimeout(state.timeoutElement);
      }
      return new Promise((resolve, reject) => {
        commit(types.SET_REQUEST_STATUS, {
          propertyName: "formNextLoading",
          status: { active: true },
        });

        let data = {
          SPID: state.selectedProvider.spid,
          ProviderName: state.selectedProvider.institutionName,
          ProviderPhoneNumber: state.selectedProvider.billingContactPhone,
          PatientAccountNumber: state.formValues.account.actNumber,
          AmountRequested: Vue.filter("USDtoFloat")(
            state.formValues.account.amtRequested
          ),
          PatientName: state.formValues.account.patientName,
          Referral: "",
          Generic1: "",
          Generic2: "",
          Generic3: "",
          Generic4: "",
          Generic5: "",
          Generic6: "",
          Generic7: "",
          Generic8: "",
          Generic9: "",
          AgreedToTerms: state.acceptedTermsAndConditions,
          AgreedToConsentToContact: state.consentToContact,
          existingHSFaccount: state.existingHSFaccount,
          FRP: {
            FullName: {
              FirstName: state.formValues.basic.firstName,
              MiddleInitial: state.formValues.basic.middleInitial,
              LastName: state.formValues.basic.lastName,
            },
            ContactInfo: {
              HomePhone: Vue.filter("numOnly")(
                state.formValues.basic.phoneNumber
              ),
              MobilePhone: Vue.filter("numOnly")(
                state.formValues.basic.mobileNumber
              ),
              WorkPhone: Vue.filter("numOnly")(
                state.formValues.basic.workNumber
              ),
              Email: state.formValues.basic.emailAddress,
            },
            Ssn: state.formValues.basic.ssn,
            DateOfBirth: state.formValues.basic.dateOfBirth,
            PhysicalAddressInfo: {
              Address1: state.formValues.physical.address1,
              Address2: state.formValues.physical.address2,
              City: state.formValues.physical.city,
              State: state.formValues.physical.state,
              ZipCode: state.formValues.physical.zipCode.split("-")[0],
              Plus4: state.formValues.physical.zipCode.split("-")[1],
            },
            PreviousAddressInfo: {
              Address1: "",
              Address2: "",
              City: "",
              State: "",
              ZipCode: "",
              Plus4: "",
            },
            MailingAddressInfo: {
              Address1: "",
              Address2: "",
              City: "",
              State: "",
              ZipCode: "",
              Plus4: "",
            },
          },
          EnrollmentSource: state.enrollmentSource == 'revspring' ? 'SelfEnrollmentPlusRevSpring' : 'SelfEnrollment',
        };

        if (state.formValues.previous) {
          data.FRP.PreviousAddressInfo = {
            Address1: state.formValues.previous.address1,
            Address2: state.formValues.previous.address2,
            City: state.formValues.previous.city,
            State: state.formValues.previous.state,
            ZipCode: state.formValues.previous.zipCode.split("-")[0],
            Plus4: state.formValues.previous.zipCode.split("-")[1],
          };
        }

        if (state.formValues.mailing) {
          data.FRP.MailingAddressInfo = {
            Address1: state.formValues.mailing.address1,
            Address2: state.formValues.mailing.address2,
            City: state.formValues.mailing.city,
            State: state.formValues.mailing.state,
            ZipCode: state.formValues.mailing.zipCode.split("-")[0],
            Plus4: state.formValues.mailing.zipCode.split("-")[1],
          };
        }

        Vue.axios
          .post(`${BASE_API_URL}/Enrollment/SubmitEnrollment`, data)
          .then((response) => {
            commit(types.SET_REQUEST_STATUS, {
              propertyName: "formNextLoading",
              status: { active: false },
            });
            // check response data
            if (
              (response.status === 200) &
              (response.data.PatientAccountNumber !== 0)
            ) {
              commit(types.SET_SUBMISSION_COMPLETE, {
                isComplete: true,
                meta: {
                  analytics: [
                    [
                      "event",
                      {
                        eventCategory: "State Change (API)",
                        eventAction: "Submission",
                        eventLabel: "Complete",
                      },
                    ],
                  ],
                },
              });
              commit(types.SET_SELF_ENROLLMENT_APP_ID, response.data.applicationID);
              resolve();
            } else {
              // handle specific errors here
              console.error(`ERROR CODE: ${response.data.apiStatusCode}`);
              event(
                "Submission Error",
                response.data.apiStatusCode,
                response.data.errorCodes.toString()
              );
              reject(`ERROR CODE: ${response.data.apiStatusCode}`);
            }
          })
          .catch((err) => {
            console.error("Error submitting enrollment.");
            commit(types.SET_REQUEST_STATUS, {
              propertyName: "formNextLoading",
              status: { active: false },
            });
            reject(err);
          });

        commit(types.SET_REQUEST_STATUS, {
          propertyName: "formNextLoading",
          status: { active: true },
        });
      });
    },
    setSubmissionComplete({ commit }, val) {
      commit(types.SET_SUBMISSION_COMPLETE, {
        isComplete: val,
      });
    },
    setCompleteForError({ commit }, reason) {
      commit(types.SET_SUBMISSION_COMPLETE, {
        isComplete: true,
        meta: {
          analytics: [
            [
              "event",
              {
                eventCategory: "State Change (API)",
                eventAction: "Error",
                eventLabel: reason,
              },
            ],
          ],
        },
      });
    },
    resetCurrentSearchStepIndex({ commit }) {
      commit(types.RESET_CURRENT_SEARCH_STEP_INDEX);
    },
    resetCurrentStepIndex({ commit }) {
      commit(types.RESET_CURRENT_STEP_INDEX);
    },
    setGuidError({ commit }, isError) {
      commit(types.SET_GUID_ERROR, { isError: isError });
    },
    setSelfEnrollmentID({ commit }, selfEnrollmentID) {
      commit(types.SET_SELF_ENROLLMENT_APP_ID, selfEnrollmentID);
    },
    setShowFeedbackModal({ commit }, value) {
      commit(types.SET_SHOW_FEEDBACK_MODAL, value);
    },
    saveFeedback: ({ state, commit }, data) => {
      return new Promise((resolve, reject) => {
        let config = {
          headers: {
            Authorization: `Bearer ${state.authToken}`
          }
        }
        Vue.axios.post(`${BASE_API_REST_URL}/Feedback/CustomerFeedback`, data, config)
          .then((response) => {
            commit(types.SET_FEEDBACK_DETAILS, data)
            resolve(response.data);
          })
          .catch((error) => {
            reject(error);
          })
      })
    },
    setEnrollmentSource({ commit }, value) {
      commit(types.SET_ENROLLMENT_SOURCE, value);
    },
    epicAuditLog() {
      return new Promise((resolve, reject) => {
        Vue.axios.get(`${BASE_API_REST_URL}/audit/epic-integration-log`)
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            reject(error);
          })
      })
    },
    fetchBillingStatementImage({ commit }, { spid }) {
      return new Promise((resolve, reject) => {
        Vue.axios.get(`${BASE_API_REST_URL}/patient-enrollment/institution/${spid}/billing-statement-image`, {
          headers: {
            'spid': spid
          }
        })
          .then((response) => {
            commit(types.SET_BILLING_STATEMENT_IMAGE, response.data);
            resolve();
          })
          .catch((error) => {
            reject(error);
          });
      })
    },
  },
  plugins: [vuexSession.plugin, analyticsMiddleware],
});
